import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Copy, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import styles from './VLAAdminShared.module.css';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import UploadDashboard from '../../Data/UploadDashboard/UploadDashboard';
import VLAAdminOperatorDetail from './VLAOperatorDetail';
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';


const CHAIN_COLORS = {
    Solana:   { bg: '#1a2a1a', color: '#4caf7d' },
    Ethereum: { bg: '#1a1a2e', color: '#7b8fd4' },
    Base:     { bg: '#0f1e2e', color: '#4e8fd4' },
    Monad:    { bg: '#1a1030', color: '#a07bd4' },
    Aptos:    { bg: '#0f2a20', color: '#4ccfa0' },
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const formatPoints = (n) => Number(n || 0).toLocaleString('en-US');
const getApplicationKey = (app) => app.application_id ?? `operator-${app.user_id}`;

// ── Small helper components ─────────────────────────────────────────────────────

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        });
    };
    return (
        <button className={styles.copyButton} onClick={handleCopy} title="Copy">
            {copied
                ? <Check size={13} className={styles.copyIconDone} />
                : <Copy size={13} className={styles.copyIcon} />}
        </button>
    );
};

const UserCell = ({ app }) => {
    if (app.wallet_address) {
        const truncated = `${app.wallet_address.slice(0, 6)}...${app.wallet_address.slice(-6)}`;
        const chainStyle = CHAIN_COLORS[app.wallet_chain] || { bg: '#2a2a2a', color: '#aaaaaa' };
        return (
            <div className={styles.userCell}>
                <div className={styles.walletAddressRow}>
                    <span className={styles.walletAddress} title={app.wallet_address}>{truncated}</span>
                    <CopyButton text={app.wallet_address} />
                </div>
                {app.wallet_chain && (
                    <span className={styles.chainBadge} style={{ background: chainStyle.bg, color: chainStyle.color }}>
                        {app.wallet_chain}
                    </span>
                )}
                {app.email && <div className={styles.userSecondary}>{app.email}</div>}
                <div className={styles.userId}>ID: {app.user_id}</div>
            </div>
        );
    }
    return (
        <div className={styles.userCell}>
            <div className={styles.userPrimary}>{app.email || '—'}</div>
            <div className={styles.userId}>ID: {app.user_id}</div>
        </div>
    );
};

const RobotList = ({ robots }) => (
    <div className={styles.robotList}>
        {robots.map((r) => (
            <div key={r.machine_id} className={styles.robotChip}>
                <span className={styles.robotModel}>{r.manufacturer} {r.model}</span>
                <span className={styles.robotSerial}>{r.serial_number}</span>
            </div>
        ))}
    </div>
);

const Pagination = ({ page, totalPages, onChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className={styles.pagination}>
            <button className={styles.pageButton} onClick={() => onChange(page - 1)} disabled={page <= 1}>
                <ChevronLeft size={14} />
            </button>
            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
            <button className={styles.pageButton} onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
                <ChevronRight size={14} />
            </button>
        </div>
    );
};

// ── Table section ─────────────────────────────────────────────────────────────

const ApplicationsTable = ({ status, collapsible, onCountChange, onOpenDashboard, onOpenDetail }) => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [apps, setApps] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [collapsed, setCollapsed] = useState(collapsible);
    const [processingKey, setProcessingKey] = useState(null);

    const getToken = () => localStorage.getItem('prismax_admin_token');

    // Cache of already-fetched pages for this table instance, keyed by page number,
    // so navigating back to a previously-viewed page doesn't refetch it.
    const pageCacheRef = useRef(new Map());

    const fetchPage = useCallback(async (p) => {
        const cached = pageCacheRef.current.get(p);
        if (cached) {
            setApps(cached.apps);
            setPage(cached.page);
            setTotalPages(cached.totalPages);
            setTotalCount(cached.totalCount);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(
                `${PRISMAX_BACKEND_URL}/api/admin/get-operator-applications?status=${status}&page=${p}`,
                { headers: { 'Authorization': `Bearer ${getToken()}` } }
            );
            const result = await res.json();
            if (result.success) {
                const { data, pagination } = result;
                setApps(data);
                setPage(pagination.page);
                setTotalPages(pagination.total_pages);
                setTotalCount(pagination.total_count);
                pageCacheRef.current.set(p, {
                    apps: data,
                    page: pagination.page,
                    totalPages: pagination.total_pages,
                    totalCount: pagination.total_count,
                });
            } else {
                const message = getApiErrorMessage(result, 'Failed to load applications.');
                if (isSessionExpiredError(message)) {
                    handleAdminSessionExpired?.();
                    return;
                }
                setError(message);
            }
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError('Failed to load applications.');
        } finally {
            setLoading(false);
        }
    }, [status, handleAdminSessionExpired]);

    // When collapsed, only the header badge count is needed, so fetch that alone.
    // When expanded, fetchPage(1) already returns the count as part of pagination,
    // so we avoid firing a second, redundant page=1 request just for the count.
    useEffect(() => {
        if (collapsed) {
            const fetchCount = async () => {
                try {
                    const res = await fetch(
                        `${PRISMAX_BACKEND_URL}/api/admin/get-operator-applications?status=${status}&page=1`,
                        { headers: { 'Authorization': `Bearer ${getToken()}` } }
                    );
                    const result = await res.json();
                    if (result.success) setTotalCount(result.pagination.total_count);
                } catch (err) {}
            };
            fetchCount();
        } else {
            fetchPage(1);
        }
    }, [collapsed, status, fetchPage]);

    const postReview = async (app, approved) => {
        const res = await fetch(`${PRISMAX_BACKEND_URL}/api/admin/review-operator-application`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ application_id: app.application_id, user_id: app.user_id, approved }),
        });
        return res.json();
    };

    const handleApprove = async (app) => {
        const applicationKey = getApplicationKey(app);
        setProcessingKey(applicationKey);
        try {
            const result = await postReview(app, true);
            if (result.success) {
                // Removing this row shifts every later page's contents, so any
                // previously cached pages are no longer accurate.
                pageCacheRef.current.clear();
                setApps((prev) => prev.filter((a) => getApplicationKey(a) !== applicationKey));
                setTotalCount((prev) => prev - 1);
                if (onCountChange) onCountChange();
            } else if (isSessionExpiredError(getApiErrorMessage(result, ''))) {
                handleAdminSessionExpired?.();
            }
        } catch (err) {}
        finally { setProcessingKey(null); }
    };

    const handleDeny = async (app) => {
        const confirmed = window.confirm(
            'Are you sure you want to deny this operator? This will remove their Operator role.'
        );
        if (!confirmed) return false;

        const applicationKey = getApplicationKey(app);
        setProcessingKey(applicationKey);
        try {
            const result = await postReview(app, false);
            if (result.success) {
                // Removing this row shifts every later page's contents, so any
                // previously cached pages are no longer accurate.
                pageCacheRef.current.clear();
                setApps((prev) => prev.filter((a) => getApplicationKey(a) !== applicationKey));
                setTotalCount((prev) => prev - 1);
                if (onCountChange) onCountChange();
                return true;
            } else if (isSessionExpiredError(getApiErrorMessage(result, ''))) {
                handleAdminSessionExpired?.();
            }
            return false;
        } catch (err) {
            return false;
        }
        finally { setProcessingKey(null); }
    };

    const openDetail = (app) => {
        if (onOpenDetail) onOpenDetail(app, () => handleDeny(app));
    };

    const isPending  = status === 'pending';
    const isApproved = status === 'approved';
    const sectionLabel = isPending ? 'Pending' : isApproved ? 'Approved' : 'Denied';

    const headers = ['User', 'Requested At', 'Status', 'Membership', 'Registered Robots', 'Actions'];

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                    {sectionLabel}
                    <span className={styles.count}>{totalCount}</span>
                </h2>
                {collapsible && (
                    <button
                        className={styles.collapseButton}
                        onClick={() => setCollapsed((prev) => !prev)}
                    >
                        {collapsed ? 'Show' : 'Hide'}
                    </button>
                )}
            </div>

            {!collapsed && (
                <>
                    {loading && <p className={styles.stateMsg}>Loading…</p>}
                    {error && <p className={styles.errorMsg}>{error}</p>}
                    {!loading && !error && apps.length === 0 && (
                        <p className={styles.empty}>No {sectionLabel.toLowerCase()} applications.</p>
                    )}
                    {!loading && !error && apps.length > 0 && (
                        <>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <colgroup>
                                        <col /><col /><col /><col /><col />
                                        <col style={{ width: '180px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>{headers.map((h) => <th key={h} className={styles.th}>{h}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {apps.map((app) => {
                                            const applicationKey = getApplicationKey(app);
                                            const isProcessing = processingKey === applicationKey;
                                            const showApprove = !isApproved;
                                            const showDeny = isPending || isApproved;
                                            return (
                                                <tr
                                                    key={applicationKey}
                                                    className={`${styles.row} ${isApproved ? styles.rowClickable : ''}`}
                                                    onClick={() => isApproved && openDetail(app)}
                                                >
                                                    <td className={styles.cell}><UserCell app={app} /></td>
                                                    <td className={styles.cell}>{formatDate(app.requested_at)}</td>
                                                    <td className={styles.cell}>
                                                        <span className={`${styles.statusBadge} ${styles[`status_${app.application_status}`]}`}>
                                                            {app.application_status}
                                                        </span>
                                                    </td>
                                                    <td className={styles.cell}>
                                                        <div className={styles.membershipCell}>
                                                            {app.user_class && (
                                                                <span className={`${styles.membershipClass} ${styles[`class_${app.user_class}`]}`}>
                                                                    {app.user_class}
                                                                </span>
                                                            )}
                                                            {app.qa_role && (
                                                                <span className={styles.chainBadge}>{app.qa_role}</span>
                                                            )}
                                                            <span className={styles.membershipPoints}>{formatPoints(app.total_points)} pts</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.cell}><RobotList robots={app.robots} /></td>
                                                    <td className={`${styles.cell} ${styles.actionCell}`}>
                                                        {isProcessing ? (
                                                            <span className={styles.actionSpinner} />
                                                        ) : (
                                                            <div className={styles.actionGroup}>
                                                                {isApproved && (
                                                                    <button
                                                                        className={styles.dashboardButton}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onOpenDashboard && onOpenDashboard(app.user_id, app.email);
                                                                        }}
                                                                        disabled={!!processingKey}
                                                                    >
                                                                        Dashboard
                                                                    </button>
                                                                )}
                                                                {showApprove && (
                                                                    <button
                                                                        className={styles.approveButton}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleApprove(app);
                                                                        }}
                                                                        disabled={!!processingKey}
                                                                    >
                                                                        Approve
                                                                    </button>
                                                                )}
                                                                {showDeny && (
                                                                    <button
                                                                        className={styles.denyButton}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeny(app);
                                                                        }}
                                                                        disabled={!!processingKey}
                                                                    >
                                                                        Deny
                                                                    </button>
                                                                )}
                                                                {isApproved && (
                                                                    <span className={styles.rowChevron}>&rsaquo;</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination page={page} totalPages={totalPages} onChange={fetchPage} />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

// ── Root component ────────────────────────────────────────────────────────────

const VLAOperators = () => {
    // Bumping these keys remounts the corresponding table, triggering a fresh count fetch
    const [approvedKey, setApprovedKey] = useState(0);
    const [deniedKey,   setDeniedKey]   = useState(0);
    const [dashboardUser, setDashboardUser] = useState(null); // { userId, email }
    const [detailApp, setDetailApp] = useState(null); // full application row for the Details modal

    const handleOpenDashboard = (userId, email) => {
        handleCloseDetail();
        setDashboardUser({ userId, email });
    };

    const handleCloseDashboard = () => {
        setDashboardUser(null);
    };

    const handleOpenDetail = (app, denyFn) => {
        setDetailApp({ app, denyFn });
    };

    const handleCloseDetail = () => {
        setDetailApp(null);
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Operators</h1>
            <hr className={styles.divider} />


            <ApplicationsTable
                key={`approved-${approvedKey}`}
                status="approved"
                collapsible={false}
                onOpenDashboard={handleOpenDashboard}
                onOpenDetail={handleOpenDetail}
                onCountChange={() => setDeniedKey((k) => k + 1)}
            />

            <ApplicationsTable
                status="pending"
                collapsible={false}
                onCountChange={() => {
                    setApprovedKey((k) => k + 1);
                    setDeniedKey((k) => k + 1);
                }}
            />

            <ApplicationsTable
                key={`denied-${deniedKey}`}
                status="denied"
                collapsible
                onCountChange={() => setApprovedKey((k) => k + 1)}
            />

            {dashboardUser && (
                <div className={styles.dashboardModalOverlay} onClick={handleCloseDashboard}>
                    <div className={styles.dashboardModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.dashboardModalHeader}>
                            <div className={styles.dashboardModalTitle}>
                                Operator Dashboard
                                {dashboardUser.email && (
                                    <span className={styles.dashboardModalEmail}>{dashboardUser.email}</span>
                                )}
                            </div>
                            <button className={styles.dashboardModalClose} onClick={handleCloseDashboard}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className={styles.dashboardModalBody}>
                            <UploadDashboard adminUserId={dashboardUser.userId} />
                        </div>
                    </div>
                </div>
            )}

            {detailApp && (
                <VLAAdminOperatorDetail
                    userId={detailApp.app.user_id}
                    email={detailApp.app.email}
                    app={detailApp.app}
                    onClose={handleCloseDetail}
                    onDeny={detailApp.denyFn}
                    onOpenDashboard={handleOpenDashboard}
                />
            )}
        </div>
    );
};

export default VLAOperators;
